import axios, { AxiosRequestConfig } from 'axios'
import * as db from '@shared/types/database'
import * as graph from '@shared/types/graphql'
import * as ui from '@/types/frontend'
import { AnalyticsMessage } from '@shared/types/analytics'
import router from '@/router'
import { uniqBy } from 'lodash-es'
import S3Assets from './S3Assets'
import {
  getSdk,
  WorkspaceWithTestsQuery,
  MgtTestQuery,
  WorkspaceWithProjectsQuery,
  OrganizationUsersQuery,
  WorkspacesByOrgIdQuery,
  WorkspacePreferencesQuery,
  FeatureFlagsQuery,
  FeatureFlagsQueryVariables,
  UpdateProjectMutation,
  CreateProjectMutation,
  UpdateAudienceMutation,
  OrganizationPriorityQuery,
  SurveyQuery,
  OrganizationWithWorkspaces
} from './graphql-sdk.generated'
import { getAuthToken } from '@/utilities/auth-token'
import AlphaGraphQLClient from './AlphaGraphQLClient'
import LogRocket from 'logrocket'
import { VUE_APP_API, VUE_APP_LEGACY_API } from '@/config'

export function getApiUrl (path: string) {
  return `${VUE_APP_API || ''}${path}`
}

const graphqlClient = new AlphaGraphQLClient(getApiUrl('/graphql'), { credentials: 'include' })
const graphqlSdk = getSdk(graphqlClient)

const transport = axios.create({
  withCredentials: true
})

const request = async <T = any>(method: AxiosRequestConfig['method'], url: string, requestData?: any) => {
  const token = getAuthToken()

  try {
    const transportObj: any = {
      url: getApiUrl(url),
      method,
      data: requestData,
      headers: {
        Authorization: token ? `JWT ${token}` : undefined,
        Accept: 'application/json',
        'Content-Type': 'application/json'
      }
    }
    const logRocketUrl = LogRocket.sessionURL
    if (logRocketUrl) transportObj['LogRocket-URL'] = logRocketUrl
    const { data } = await transport(transportObj)
    return Promise.resolve(data as T)
  } catch (err) {
    if (err.response && err.response.status === 401 && router.currentRoute.path !== '/login') {
      const path = window.location.pathname + window.location.search + window.location.hash
      router.push({ name: 'login', params: { continue: path } })
    }
    return Promise.reject(err)
  }
}

export interface MGTResponse<T = any> {
  status: number,
  headers: any,
  data: T
}

export interface RouteIds {
  workspaceId: string
  testId: string
  projectId: string
}

export default {
  createTemplate (workspaceId: string, data: any) {
    return request<MGTResponse>('post', '/api/test_template/' + workspaceId, data)
  },
  async getMgtTest (workspaceId: string, testId: string): Promise<MgtTestQuery['mgtTest']> {
    const res = await graphqlSdk.mgtTest({ workspaceId, testId })
    return res.mgtTest
  },
  async getWorkspaceWithTests (workspaceId: string): Promise<WorkspaceWithTestsQuery['workspace']> {
    // This is failing if the api and reef get out of sync. Commenting to troubleshoot
    const res = await graphqlSdk.workspaceWithTests({ id: workspaceId })
    return res.workspace
  },
  async getWorkspaceWithProjects (workspaceId: string): Promise<WorkspaceWithProjectsQuery['workspace']> {
    const res = await graphqlSdk.workspaceWithProjects({ id: workspaceId })
    return res.workspace
  },
  async getSharedPrototypes (workspaceId: string, projectId: string): Promise<graph.Prototype[]> {
    const res = await graphqlSdk.sharedPrototypes({ workspaceId, projectId })
    return uniqBy(res.sharedPrototypes, (prototype: graph.Prototype) => [prototype.title.trim(), prototype.url?.trim()].join())
  },
  async getUserAcl (groupId: string): Promise<graph.GroupsPermission> {
    const res = await graphqlSdk.userPermission({ groupId })
    return res.userPermission
  },
  async getFeatureConfig (vars: FeatureFlagsQueryVariables): Promise<FeatureFlagsQuery['featureFlags']> {
    const res = await graphqlSdk.featureFlags(vars)
    return res.featureFlags
  },
  async getTemplates (accountId: string): Promise<graph.Template[]> {
    const res = await graphqlSdk.templatesByAccount({ accountId })
    return res.templatesByAccount
  },
  async getTemplateById (id: string, accountId: string): Promise<graph.Template> {
    const res = await graphqlSdk.template({ id, accountId })
    return res.template
  },
  async createTest (workspaceId: string, payload: graph.CreateTestInput) {
    const res = await graphqlSdk.createTest({ workspaceId, payload })
    return res.createTest
  },
  async getSharedReports (workspaceId: string, reportIds?: [string]): Promise<graph.SharedReports[]> {
    const res = await graphqlSdk.sharedReports({ workspaceId, reportIds })
    return res.sharedReports
  },
  async getSession (): Promise<graph.Session | undefined | null> {
    const res = await graphqlSdk.session()
    return res.me
  },
  async getOrganizationUsers (orgId: string): Promise<OrganizationUsersQuery['organizationUsers']> {
    const res = await graphqlSdk.organizationUsers({ orgId })
    return res.organizationUsers
  },
  async getOrganizationWorkspaces (orgId: string) : Promise<WorkspacesByOrgIdQuery['workspacesByOrgId']> {
    const res = await graphqlSdk.workspacesByOrgId({ orgId })
    return res.workspacesByOrgId
  },
  async getWorkspacePreferences (workspaceId: string) : Promise<WorkspacePreferencesQuery> {
    return graphqlSdk.workspacePreferences({ workspaceId })
  },
  async updateTest (workspaceId: string, testId: string, payload: graph.UpdateTestInput) {
    const res = await graphqlSdk.updateTest({ workspaceId, testId, payload })
    return res.updateTest
  },
  async getOrganizations (accountId: string): Promise<Pick<OrganizationWithWorkspaces, 'id' | 'name'>[]> {
    const res = await graphqlSdk.account({ accountId })
    return res.account?.organizations || []
  },
  deleteTest (ids: Pick<RouteIds, 'workspaceId' | 'testId'>) {
    const { workspaceId, testId } = ids
    return request<MGTResponse>('post', `/api/${workspaceId}/${testId}/hide`)
  },
  login (loginRequest: db.LoginRequest): Promise<{ token: string, session: db.UserSession, redirect?: string}> {
    return request('post', '/auth/login', loginRequest)
  },
  logout (): Promise<any> {
    return request('post', '/auth/logout')
  },
  loginSaml (email: string) {
    type LoginSamlResponse = MGTResponse<{ next?: string, msg: string }>
    const payload = { email, auth_type: 'saml', fromOcean: true }
    return request<LoginSamlResponse>('post', '/auth/login/saml', payload)
  },
  async addComment (payload: ui.CreateCommentInput, ids: Pick<RouteIds, 'workspaceId' | 'testId'>) {
    const { workspaceId, testId } = ids
    const commentPayload: graph.UserCommentInput = {
      comment: payload.comment,
      taggedUsers: payload.tagged_user_ids
    }
    const { createComment } = await graphqlSdk.createComment({ workspaceId, testId, payload: commentPayload })
    return createComment
  },
  editComment (payload: ui.EditCommentInput, ids: Pick<RouteIds, 'workspaceId' | 'testId'>) {
    const { workspaceId, testId } = ids
    return request<MGTResponse>('post', `/api/${workspaceId}/${testId}/edit-comment`, payload)
  },
  deleteComment (payload: ui.DeleteCommentInput, ids: Pick<RouteIds, 'workspaceId' | 'testId'>) {
    const { workspaceId, testId } = ids
    return request<MGTResponse>('post', `/api/${workspaceId}/${testId}/delete-comment`, payload)
  },
  addTestToProject (payload: string, ids: RouteIds) {
    const { workspaceId, projectId } = ids
    return request<MGTResponse>('post', `/api/${workspaceId}/reports/${projectId}`, payload)
  },
  updateQualNotable (payload: string, ids: Pick<RouteIds, 'workspaceId' | 'testId'>) {
    const { workspaceId, testId } = ids
    return request<MGTResponse>('post', `/api/${workspaceId}/${testId}/update/qualNotable`, payload)
  },
  updateAssetUrl (payload: string, ids: Pick<RouteIds, 'workspaceId' | 'testId'> & { assetId: string }) {
    const { workspaceId, testId, assetId } = ids
    return request<MGTResponse>('post', `/api/${workspaceId}/${testId}/${assetId}/edit/url`, payload)
  },
  sendFeedback (rating: 'none' | 'good' | 'bad', text: string, ids: RouteIds) : Promise<any> {
    const { workspaceId, testId } = ids
    const payload = JSON.stringify({
      rating: rating,
      text: text
    })

    return request<MGTResponse>('post', `/api/${workspaceId}/${testId}/feedback`, payload)
  },
  async getTextResponse (s3key: string): Promise<{ filters: any}> {
    const { data } = await S3Assets.get(`${s3key}_text_responses.json`)
    return data
  },
  async updateTextResponse (s3key: string, payload:string): Promise<any> {
    const { data } = await S3Assets.update(`${s3key}_text_responses.json`, payload)
    return data
  },
  getTextAsset (url: string) {
    let id = url.split('/').pop()
    // Account for some urls that include _text_responses.json, remove those to get correct ID. Bug ID: 851
    if (id?.includes('_')) {
      id = id.split('_').shift()
    }
    return request<MGTResponse>('get', `/api/staticassets/text/${id}`)
  },
  sharedReportPreviewProjects (workspaceId: string, projectId: string) {
    return request<MGTResponse>('get', `/api/${workspaceId}/experiment/${projectId}/SharePreview`)
  },
  sharedReportPreviewTests (workspaceId: string, cardId: string) {
    return request('get', `/api/${workspaceId}/${cardId}/SharePreview/`)
  },
  viewSharedReport (reportId: string) {
    const url = `${VUE_APP_LEGACY_API}/report/${reportId}/?reef=true`
    return axios.get(url)
  },
  editSharedReport (workspaceId: string, reportId: string) {
    return request<MGTResponse>('get', `/api/${workspaceId}/report/${reportId}/edit`)
  },
  publishEditReport (baseUrl: string, data: any) {
    const url = `/api${baseUrl}`
    return request('post', url, data)
  },
  deleteReport (baseUrl: string) {
    const url = `/api${baseUrl}`
    return request('post', url)
  },
  editSharedReportFileName (workspaceId:string, reference:string, data:any) {
    const url = `/api/${workspaceId}/report/${reference}/editFilename`
    return request('post', url, data)
  },
  shareSharedReport (data:any) {
    const url = '/api/intents/share'
    return request('post', url, data)
  },
  interestSharedReport (data:any) {
    const url = '/api/intents/interest'
    return request('post', url, data)
  },
  async checkResetPasswordToken (token: string) {
    const resp = await graphqlSdk.checkResetPasswordToken({ token })
    return resp.checkResetPasswordToken
  },
  async priority (orgId: string, workspaceId?: string): Promise<OrganizationPriorityQuery['organizationPriority']> {
    const res = await graphqlSdk.organizationPriority({ orgId, workspaceId })
    return res.organizationPriority
  },
  prioritize (orgId: string, payload: any): Promise<any> {
    return request('put', `/api/${orgId}/priority`, payload)
  },
  async changePassword (userId: string, payload:{ newPassword: string, oldPassword: string}) {
    const resp = await graphqlSdk.changePassword({ userId, payload })
    return resp.changePassword
  },
  async resetPassword (payload: {token: string, userId: string, password: string}) {
    const resp = await graphqlSdk.resetPassword(payload)
    return resp.resetPassword
  },
  async forgotPasswordEmail (email: string) {
    const resp = await graphqlSdk.forgotPasswordEmail({ email })
    return resp.forgotPasswordEmail
  },
  createAnalyticsMessage (message: AnalyticsMessage) {
    return request('post', '/api/measurement/messages', JSON.stringify(message))
  },
  getAudiencesById (workspaceId: string, accountId: string, audiencesToFetch: string[]) {
    const url = `/api/${workspaceId}/audiences-by-id/${accountId}`
    return request<MGTResponse>('post', url, audiencesToFetch)
  },
  async getAudienceById (id: string, accountId: string) {
    const res = await graphqlSdk.audience({ id, accountId })
    return res.audience
  },
  async getAudiencesTestCount (accountId: string, audienceIds: string[]) {
    const res = await graphqlSdk.audienceTestCounts({ accountId, audienceIds })
    return res
  },
  createAudience (workspaceId: string, formattedAudienceForRR: db.FormattedAudienceForRR) {
    const url = `/api/${workspaceId}/create-audience`
    return request<MGTResponse>('post', url, formattedAudienceForRR)
  },
  updateAudience (workspaceId: string, survey: any) {
    const url = `/api/${workspaceId}/update-audience`
    return request<MGTResponse>('post', url, survey)
  },
  async updateAudienceDepths (accountId: string, audienceId: string, audience: graph.AudienceInput): Promise<UpdateAudienceMutation> {
    const res = await graphqlSdk.updateAudience({ accountId, audienceId, payload: audience })
    return res
  },
  getLegacyAudience (workspaceId: string, audience: string) {
    const url = `/api/${workspaceId}/legacy-audience/${encodeURIComponent(audience.replace(/\//gi, '~2F'))}`
    return request<MGTResponse>('get', url)
  },

  subscribeGroup (workspaceId: string, testId: string, userIds: string[], testTitle: string) {
    const url = `/api/${workspaceId}/${testId}/subscribe-group`
    return request<MGTResponse>('post', url, {
      newFollowingUserIds: userIds,
      test_title: testTitle
    })
  },
  updateWorkspaceSetting (workspaceId: string, section: string, data: object): Promise<any> {
    const url = `/api/${workspaceId}/settings/${section}`
    return request<MGTResponse>('post', url, data)
  },
  uploadFile (file: File, key: string) {
    return S3Assets.upload(key, file)
  },
  async createProject (workspaceId: string, title: string, description?: string): Promise<CreateProjectMutation['createProject']> {
    const project = {
      title,
      desc: description || ''
    }
    const { createProject } = await graphqlSdk.createProject({ workspaceId, payload: project })
    return createProject
  },
  async updateProject (workspaceId: string, projectId: string, project: graph.UpdateProjectInput): Promise<UpdateProjectMutation> {
    const res = await graphqlSdk.updateProject({ workspaceId, projectId, payload: project })
    return res
  },

  async updateBranding (workspaceId: string, brandingPayload: Omit<graph.Branding, 'id' | 'createdAt' | 'workspaceId'>): Promise<graph.Branding | undefined> {
    const res = await graphqlSdk.updateBranding({ workspaceId, payload: brandingPayload })
    return res.updateBranding
  },

  async deleteAssets (workspaceId: string, testId: string, assetKeys: string[]) {
    await Promise.all(assetKeys.map(assetKey => {
      const url = `/api/${workspaceId}/${testId}/${assetKey}/delete`
      return request<MGTResponse>('post', url)
    }))
  },
  async getSurvey (workspaceId: string, testId: string, sharedLinkHash?: string, surveyId?: string, questionIds?: string[]): Promise<SurveyQuery['survey']> {
    const res = await graphqlSdk.survey({ workspaceId, testId, sharedLinkHash })
    return res.survey
  },
  async manageResponses (payload: graph.MutationManageResponsesArgs) {
    const res = await graphqlSdk.manageResponses(payload)
    return res.manageResponses
  },
  async currentReleaseVersion (): Promise<string> {
    const res = await request<{ currentRelease: string }>('get', '/releases/current')
    return res.currentRelease
  },
  async createCsv (workspaceId:string, surveyId?:string) {
    if (!surveyId) {
      console.error('Tried to create a csv without a survey Id', workspaceId)
      return
    }
    const resp = await graphqlSdk.createCsv({ workspaceId, surveyId })
    return resp.createCsv
  },
  async createScreenerCsv (workspaceId:string, testId:string) {
    const resp = await graphqlSdk.createScreenerCsv({ workspaceId, testId })
    return resp.createScreenerCsv
  },
  async getTestConfiguration (args:graph.QueryTestConfigArgs) {
    const resp = await graphqlSdk.testConfig(args)
    return resp.testConfig
  },
  async getPublicTestConfiguration (args:graph.QueryTestConfigArgs) {
    const resp = await graphqlSdk.testConfigPublic(args)
    return resp.testConfig
  },
  async upsertTestConfig (payload: graph.TestConfigInput) {
    const resp = await graphqlSdk.createTestConfig({ payload })
    return resp.createTestConfig
  },
  async toggleSharedLink (payload: {workspaceId: string, testId: string, createLink: boolean}) {
    const resp = await graphqlSdk.toggleSharedLink({ ...payload })
    return resp.toggleSharedLink
  },
  async searchResponses (payload: { questionId: string, workspaceId: string, searchTerm: string, sharedLinkHash: string | undefined}) {
    const resp = await graphqlSdk.searchResponses(payload)
    return resp.searchResponses
  },
  async signAssetUrl (url: string) {
    return await request<string>('PUT', '/assets/sign-asset-url', { url })
  }
}
